export type ViewMode = "table" | "gantt" | "kanban" | "dashboard"

export interface EngagementFinanceRow {
  id: number
  engagementName: string | null
  engagementCode: string | null
  engagementStatus: string | null
  billingType: string | null
  requestType: string | null
  startDate: string | null
  endDate: string | null
  /**
   * The engagement's own currency (`coreInfo.currencyCode`). Read by name off `row.original` by the
   * grid's cell formatter and by the group-total currency guard, so the field name is load-bearing.
   *
   * Null only where the engagement has no currency set, or where its code is not one
   * geo.CodeCurrency knows. It is not withheld on a currency mismatch: every figure that survives its
   * own gate below is denominated in this currency, so the label follows the figure. Not the same
   * currency as `EngagementTimeEntriesRow.currency` (the work order's bill currency) or
   * `EngagementPeopleRow.currency` (the worker's payment currency), which can differ from this one.
   */
  currency: string | null
  /**
   * Read straight off `coreInfo`, so these two are in the engagement's currency whatever the
   * transactions under it are denominated in, and never withheld.
   */
  contractAmount: number
  projectedCost: number
  /**
   * Every money figure from here down is null where the streams it sums disagree on a currency.
   *
   * Each is a sum or a difference over the billing, payment and expense streams, none of which is
   * constrained to the currency the engagement carries, so on a mismatch each would be a total of
   * unlike quantities. They report nothing rather than a figure, and render as "—".
   *
   * The condition belongs to the procedure, not the client, and it is per side: each figure withdraws
   * where the streams it actually draws on disagree, not wherever the engagement disagrees at all. A
   * bill-side disagreement takes the revenue figures, Unbilled Revenue and Remaining Contract Amount;
   * a pay-side one takes the cost figures and % of Actual to Projected. Two follow the billing type,
   * because on Milestone recognition is the coreInfo schedule rather than what was billed: Gross
   * Profit withdraws on a pay-side disagreement there and on either one otherwise, and Revenue
   * Recognition to Date survives a bill-side disagreement there and not otherwise. So a row can
   * arrive with its revenue populated and its cost null, or the reverse.
   *
   * `currency` above is never withheld alongside them, and is no evidence that nothing was.
   */
  billedToDate: number | null
  revenueRecognitionToDate: number | null
  actualCostToDate: number | null
  billableCost: number | null
  nonBillableCost: number | null
  percentActualToProjected: number | null
  grossProfit: number | null
  unbilledRevenue: number | null
  remainingContractAmount: number | null
  engagementType: string | null
  engagementLead: string | null
  deliveryManager: string | null
  branch: string | null
  client: string | null
  projectedHours: number
  hoursToDate: number
  numberOfResources: number
  /** Period slices of the billing and payment streams, withheld with them. */
  currentMonthRevenue: number | null
  currentMonthCost: number | null
  ytdRevenue: number | null
  ytdCost: number | null
  projectToDateRevenue: number | null
  projectToDateCost: number | null
  asOfDate: string | null
  /**
   * The root of this engagement's change-request chain, formatted `Name (RecordId)`. This is the
   * grouping key behind the "Engagement roll-up" option and the grid groups on the value itself, so
   * the RecordId suffix is load-bearing — engagement names are free text and two unrelated
   * engagements sharing a name would otherwise merge into one group. An engagement with no chain
   * resolves to itself; members of a cyclic pointer chain each resolve to themselves.
   */
  rollUpEngagement: string | null
}

export interface EngagementHoursRow {
  userProfileId: number
  workerName: string | null
  workerType: string | null
  engagementCode: string | null
  engagementStartDate: string | null
  engagementEndDate: string | null
  billingType: string | null
  plannedHours: number
  actualHours: number
  varianceHours: number
  variancePercent: number | null
  billableHours: number
  nonBillableHours: number
  billableTotalPercent: number | null
  nonBillableTotalPercent: number | null
  jobTitle: string | null
  functionalManager: string | null
  engagementLead: string | null
  deliveryManager: string | null
  /** See EngagementFinanceRow.rollUpEngagement */
  rollUpEngagement: string | null
}

export interface EngagementTimeEntriesRow {
  date: string | null
  client: string | null
  engagementName: string | null
  engagementCode: string | null
  task: string | null
  notes: string | null
  hours: number
  isBillable: boolean
  isInvoiced: boolean
  isApproved: boolean
  firstName: string | null
  lastName: string | null
  userProfileId: number
  jobTitle: string | null
  workerType: string | null
  t4Type: string | null
  billableRate: number
  billableAmount: number
  payRate: number
  payAmount: number
  /**
   * The bill-side currency, from the work order version's BillingInfo. Covers Bill Rate, Bill Amount
   * and Revenue per day.
   *
   * Not renamed to `billCurrency` despite what it holds: it is this report's default currency field,
   * and the column id and every saved view built on it key off the name.
   */
  currency: string | null
  /**
   * The pay-side currency, from the work order version's PaymentInfo. Covers Pay Rate, Pay Amount
   * and COGS per day.
   *
   * Equal to `currency` on most rows. The two are allowed to differ: the work order's
   * SameBillAndPayCurrency rule raises a Warning rather than blocking the save.
   */
  payCurrency: string | null
  /**
   * The engagement's own currency. Covers Reporting Bill Rate, which is read from the engagement's
   * worker record rather than from the work order, and so follows neither work order currency.
   */
  engagementCurrency: string | null
  reportingBillRate: number
  cogsPerDay: number
  revenuePerDay: number
  /**
   * Bill-side revenue minus pay-side cost, so it only means anything while `currency` and
   * `payCurrency` agree. Null where they provably differ, since the subtraction would be across two
   * currencies. Null renders as "—", which is why this is nullable where the figures beside it are
   * not.
   */
  gpPerDay: number | null
  procurementType: string | null
  practice: string | null
  branch: string | null
  functionalManager: string | null
  engagementLead: string | null
  deliveryManager: string | null
  workerLocation: string | null
  engagementType: string | null
  engagementStartDate: string | null
  engagementEndDate: string | null
  employmentEndDate: string | null
  isUtilized: boolean
  isPto: boolean
  isStatHoliday: boolean
  isPayable: boolean
  timesheetApproverName: string | null
  /** The IMMEDIATE source engagement (one hop). Distinct from rollUpEngagement, which walks to the root. */
  parentEngagementCode: string | null
  parentEngagementName: string | null
  /** See EngagementFinanceRow.rollUpEngagement */
  rollUpEngagement: string | null
}

export interface EngagementPeopleRow {
  userProfileId: number
  workerName: string | null
  workerType: string | null
  functionalManager: string | null
  lineOfBusiness: string | null
  procurementSource: string | null
  profileStatus: string | null
  engagementUtilizationPercent: number | null
  plannedHours: number
  totalApprovedHoursToDate: number
  vacationTakenToDate: number
  skills: string | null
  certifications: string | null
  jobTitle: string | null
  workerLocation: string | null
  payRate: number
  totalBillableHours: number
  totalNonBillableHours: number
  hireDate: string | null
  employmentEndDate: string | null
  conversionDate: string | null
  tenureMonths: number | null
  firstEngagementStartDate: string | null
  lastEngagementEndDate: string | null
  timeEntryStartDate: string | null
  timeEntryEndDate: string | null
  currency: string | null
}

export type ReportFilters = Record<string, string | number | null | undefined>

export interface ReportConfig {
  code: string
  label: string
  path: string
  apiEndpoint: string
  enabled: boolean
}

export const REPORT_CONFIGS: ReportConfig[] = [
  { code: "engagement-finance", label: "Finance", path: "/engagements/finance", apiEndpoint: "/api/engagement-reports/finance", enabled: true },
  { code: "engagement-hours", label: "Hours", path: "/engagements/hours", apiEndpoint: "/api/engagement-reports/hours", enabled: true },
  { code: "engagement-people", label: "People", path: "/engagements/people", apiEndpoint: "/api/engagement-reports/people", enabled: true },
  { code: "engagement-time-entries", label: "Time Entries", path: "/engagements/time-entries", apiEndpoint: "/api/engagement-reports/time-entries", enabled: true },
]

export interface SharedReportListItem {
  userProfileSearchStateId: number
  userProfileIdOwner: number
  fullName: string
  stateName: string
  stateDescription: string
  componentName: string
  isPublic: boolean
  userProfileIds: number[]
  teamGuids: string[]
}

export interface GridState {
  columnVisibility: Record<string, boolean>
  columnOrder: string[]
  columnSizing: Record<string, number>
  sorting: { id: string; desc: boolean }[]
  columnFilters: { id: string; value: unknown }[]
  globalFilter: string
  pagination: { pageSize: number }
  grouping?: string[]
}
